import { SportType } from '../../court/dto/court.dto';

export type UserMembershipDto = {
  id: string;
  userId: string;
  type: UserMembershipTypeDto;
  startDate: string;
  endDate?: string;
  isActive: boolean;
  // MYS-117 Duo (hand-synced from api). True when this membership was minted
  // for an attached beneficiary of someone else's paying subscription.
  // payerUserId is the payer's USER ID only — no name/email (coach-reachable
  // surface); resolve display names from data the viewer already has.
  isAttachedBeneficiary?: boolean;
  payerUserId?: string;
};

// MYS-117 Duo (hand-synced from api): one payer→beneficiary attachment.
export type SubscriptionBeneficiaryDto = {
  id: string;
  subscriptionId: string;
  userId?: string;
  attachedAt: string;
  attachedByUserId?: string;
  detachedAt?: string;
};

export type CreateUserMembershipDto = Pick<
  UserMembershipDto,
  'startDate' | 'endDate' | 'isActive'
> & { membershipTypeId: string };

export type UpdateUserMembershipDto = Pick<
  UserMembershipDto,
  'startDate' | 'endDate' | 'isActive'
> & { membershipTypeId: string };

export enum UserMembershipDiscountType {
  PERCENTAGE = 'percentage',
  CONSTANT = 'constant',
}

export enum MembershipBillingInterval {
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
}

// MYS-125 (hand-synced from api). Mirror of
// api/src/user-membership/entity/membership-variant.entity.ts's
// MembershipVariantPaymentMode.
export enum MembershipVariantPaymentMode {
  RECURRING = 'recurring',
  SINGLE = 'single',
}

// MYS-125 (hand-synced from api): a sous-tarif (sub-tariff) declension of a
// UserMembershipType — price/payment-mode/commitment only, never
// entitlements (those stay on the type). priceInCents is minor units (cents),
// same convention as UserMembershipTypeDto.priceInCents.
export type MembershipVariantDto = {
  id: string;
  membershipTypeId: string;
  label: string;
  priceInCents: number;
  currency: string;
  paymentMode: MembershipVariantPaymentMode;
  commitmentMonths: number;
  isDefault: boolean;
  // The 0€ non-paying half of a Duo (MYS-117) — granted automatically via
  // Duo attach, never purchasable directly. Display-only.
  isDuoBeneficiary: boolean;
  // MYS-125 P4.3/P4.9 — D6's resolution table, partner-side half. Names
  // which partner UserMembershipType.name values this variant serves.
  // Editable via MembershipVariantsSection.tsx's multi-select (P4.9).
  duoPartnerTypeKeys?: string[];
  stripePriceId?: string;
};

// MYS-125 (hand-synced from api/src/user-membership/entity/membership-variant.entity.ts).
// The only allowed commitment lengths (D5) — shared by the create/update form's
// select options and its client-side validation, mirroring the API's own
// ALLOWED_COMMITMENT_MONTHS/CommitmentMonths so the two cannot drift.
export const ALLOWED_COMMITMENT_MONTHS = [1, 3, 6, 12] as const;
export type CommitmentMonths = (typeof ALLOWED_COMMITMENT_MONTHS)[number];

// MYS-125 P2.1 (hand-synced from api): request bodies for
// POST/PUT organizations/:typeId/variants(/:variantId). The API's decorators
// are advisory only (SoftValidationPipe) — the real checks live server-side
// in assertMembershipVariantFields, so the dashboard form mirrors them
// client-side purely for a legible inline error, not as the source of truth.
export type CreateMembershipVariantDto = {
  label: string;
  // Minor units (cents) — same convention as MembershipVariantDto.priceInCents.
  priceInCents: number;
  currency: string;
  paymentMode: MembershipVariantPaymentMode;
  commitmentMonths: number;
  isDefault?: boolean;
  isDuoBeneficiary?: boolean;
  // MYS-125 P4.3/P4.9 — D6's Duo resolution table, partner-side half. Names
  // which partner UserMembershipType.name values this variant serves.
  // Mutually exclusive with isDuoBeneficiary — hard-checked server-side in
  // assertMembershipVariantFields, mirrored client-side only for a legible
  // inline error (MembershipVariantsSection.tsx).
  duoPartnerTypeKeys?: string[];
};

export type UpdateMembershipVariantDto = CreateMembershipVariantDto;

// Mirror of api/src/user-membership/entity/cgu-source.enum.ts. Hand-synced
// because user-membership.dto.ts is not auto-copied by dto-syncer (entity import).
export enum CguSource {
  ORGANIZATION = 'organization',
  MEMBERSHIP_TYPE = 'membership_type',
}

// Response from GET /organizations/:orgId/membership-types/:typeId/cgu.
// 204 (no body) when neither org nor type has a CGU configured.
export type CguDto = {
  text: string;
  version: number;
  source: CguSource;
};

// Per-type CGU override edit. cguText: null clears the override (falls back to org).
export class UpdateMembershipTypeCguDto {
  cguText?: string | null;
}

export type CreateTimeWindowDiscountDto = Omit<TimeWindowDiscountDto, 'id' | 'timeWindowGroupName'>;

export type TimeWindowDiscountDto = {
  id: string;
  timeWindowGroupId: string;
  timeWindowGroupName: string;
  courtBookingDiscountType: UserMembershipDiscountType;
  courtBookingDiscountValue: number;
  maxPrivateCourtBookings: number;
  maxPrivateCourtBookingsDuration: number;
  maxCourtBookingTimeInAdvance: number;
};

// Per-sport court-booking discount override. A matching sport override beats
// the time-window override and the default discount.
export type SportDiscountDto = {
  sport: SportType;
  courtBookingDiscountType: UserMembershipDiscountType;
  courtBookingDiscountValue: number;
};

export type MembershipDescriptionSection = {
  header?: string | null;
  bullets: string[];
};

export const MEMBERSHIP_DESCRIPTION_MAX_SECTIONS = 5;
export const MEMBERSHIP_DESCRIPTION_MAX_BULLETS_PER_SECTION = 8;
export const MEMBERSHIP_DESCRIPTION_MAX_BULLET_LENGTH = 160;
export const MEMBERSHIP_DESCRIPTION_MAX_HEADER_LENGTH = 60;

// MYS-27 — how many bullets the mobile card shows before the "+N" reveal.
// The split is derived purely from section order then bullet order, so an
// operator controls what is "shown in priority" by reordering in the dashboard
// editor (which previews the same cut-off using this constant). Keep in sync
// with the mirror copies in api/mobile.
export const MEMBERSHIP_DESCRIPTION_PREVIEW_BULLETS = 4;

export type UserMembershipTypeDto = {
  id: string;
  name: string;
  description?: string;
  descriptionSections?: MembershipDescriptionSection[] | null;
  timeWindowDiscounts: TimeWindowDiscountDto[];
  coachingSessionDiscountType: UserMembershipDiscountType;
  coachingSessionDiscountValue: number;
  maxCourtBookingTimeInAdvance: number;
  organizationId: string;
  maxPrivateCourtBookings: number;
  maxPrivateCourtBookingsDuration: number;
  isOrganizationDefault: boolean;
  courtBookingDiscountType: UserMembershipDiscountType;
  courtBookingDiscountValue: number;
  // MYS-122: discount on the price of joining an event (class, competition).
  // PERCENTAGE = whole percent 0-100; CONSTANT = whole MAJOR currency units
  // (euros), never cents. Both columns are NOT NULL with a 0 default, so the
  // API always returns the pair. Hand-synced: dto:sync classifies
  // user-membership/dto as backend-only and never copies this file.
  eventJoiningDiscountType: UserMembershipDiscountType;
  eventJoiningDiscountValue: number;
  // Sports this membership applies to. null or [] = all sports.
  sports?: SportType[] | null;
  // Per-sport discount overrides. null or [] = uniform discount.
  sportDiscounts?: SportDiscountDto[] | null;
  // Subscription fields
  priceInCents?: number;
  currency?: string;
  billingInterval?: MembershipBillingInterval;
  stripeProductId?: string;
  stripePriceId?: string;
  isSubscriptionBased: boolean;
  // MYS-117 Duo: extra beneficiaries a paying subscription may attach
  // (0/undefined = solo plan, 1 = Duo).
  maxAttachedMembers?: number;
  // CGU override version (monotonic, default 0). cguText is fetched on-demand
  // via the GET /cgu endpoint and never embedded here.
  cguVersion?: number;
  // MYS-125: live sub-tariffs (variants) of this offer. Populated only when
  // the API loaded the `variants` relation (get-membership-types.ts) — []
  // when not, same degrade-gracefully shape as timeWindowDiscounts. A type
  // with no live variants is pre-MYS-125 legacy (or brand new); clients fall
  // back to the type-level priceInCents.
  variants?: MembershipVariantDto[];
};

export type CreateUserMembershipTypeDto = {
  name: string;
  description?: string;
  descriptionSections?: MembershipDescriptionSection[] | null;
  timeWindowDiscounts: Array<{
    timeWindowGroupId: string;
    courtBookingDiscountType: UserMembershipDiscountType;
    courtBookingDiscountValue: number;
    maxPrivateCourtBookings: number;
    maxPrivateCourtBookingsDuration: number;
    maxCourtBookingTimeInAdvance: number;
  }>;
  coachingSessionDiscountType: UserMembershipDiscountType;
  coachingSessionDiscountValue: number;
  maxCourtBookingTimeInAdvance: number;
  maxPrivateCourtBookings: number;
  maxPrivateCourtBookingsDuration: number;
  courtBookingDiscountType: UserMembershipDiscountType;
  courtBookingDiscountValue: number;
  // MYS-122. Optional to mirror the API DTO: omitted = no event discount.
  // See UserMembershipTypeDto for the units.
  eventJoiningDiscountType?: UserMembershipDiscountType;
  eventJoiningDiscountValue?: number;
  // Sports this membership applies to. null or [] = all sports.
  sports?: SportType[] | null;
  // Per-sport discount overrides. null or [] = uniform discount.
  sportDiscounts?: SportDiscountDto[] | null;
  // Subscription fields
  priceInCents?: number;
  currency?: string;
  billingInterval?: MembershipBillingInterval;
  isSubscriptionBased?: boolean;
  // MYS-117 Duo: 0/omitted = solo, 1 = Duo. Label "Duo" via i18n keyed on
  // this value; the API hard-caps at MAX_ATTACHED_MEMBERS_CEILING (1).
  maxAttachedMembers?: number;
};

export type UpdateUserMembershipTypeDto = Pick<
  UserMembershipTypeDto,
  | 'name'
  | 'description'
  | 'descriptionSections'
  | 'timeWindowDiscounts'
  | 'coachingSessionDiscountType'
  | 'coachingSessionDiscountValue'
  | 'courtBookingDiscountType'
  | 'courtBookingDiscountValue'
  | 'maxCourtBookingTimeInAdvance'
  | 'maxPrivateCourtBookings'
  | 'maxPrivateCourtBookingsDuration'
  | 'sports'
  | 'sportDiscounts'
> & {
  // MYS-122. Deliberately NOT in the Pick above: the API treats an omitted
  // pair as "leave the stored discount untouched", so keeping it optional here
  // preserves that contract for any caller that builds a partial update.
  eventJoiningDiscountType?: UserMembershipDiscountType;
  eventJoiningDiscountValue?: number;
  priceInCents?: number;
  currency?: string;
  billingInterval?: MembershipBillingInterval;
  isSubscriptionBased?: boolean;
  // MYS-117 Duo: omit-means-untouched (an older build must not reset a Duo
  // plan to solo). API hard-caps at 1.
  maxAttachedMembers?: number;
};

export const MAX_COURT_BOOKING_TIME_IN_ADVANCE_MINIMUM_DAYS = 1;

export enum SubscriptionStatus {
  ACTIVE = 'active',
  CANCELED = 'canceled',
  INCOMPLETE = 'incomplete',
  INCOMPLETE_EXPIRED = 'incomplete_expired',
  PAST_DUE = 'past_due',
  PAUSED = 'paused',
  TRIALING = 'trialing',
  UNPAID = 'unpaid',
}

export type MembershipSubscriptionDto = {
  id: string;
  userId: string;
  membershipType: UserMembershipTypeDto;
  // MYS-125 P2.2a (hand-synced from api): the sub-tariff this subscription is
  // actually charged at. Present only when the API populated the `variant`
  // relation (get-users-with-memberships.ts, the query behind
  // GET users/with-memberships) — undefined on legacy pre-variants
  // subscriptions, same degrade-gracefully shape as UserMembershipTypeDto.variants.
  variant?: MembershipVariantDto;
  stripeSubscriptionId: string;
  status: SubscriptionStatus;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  canceledAt?: string;
  cancelAtPeriodEnd?: boolean;
  trialStart?: string;
  trialEnd?: string;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  isExpired: boolean;
  daysUntilExpiration: number;
  metadata?: Record<string, any>;
  // CGU acceptance metadata. Snapshot body excluded from the default DTO; only
  // admin dispute-triage endpoint returns it. NULL on legacy pre-CGU subs.
  acceptedCguVersion?: number;
  acceptedCguSource?: CguSource;
  acceptedCguAt?: string;
  // MYS-117 Duo (hand-synced): the currently-attached beneficiary, if any.
  // Names OK on this DTO (owner + admin surfaces only).
  attachedUser?: {
    id: string;
    firstName?: string;
    lastName?: string;
  };
  attachedAt?: string;
  attachedByUserId?: string;
};

// MYS-117 Duo: bodies for PUT /membership-subscriptions/:id/attached-user[/admin].
export type SetAttachedUserDto = {
  attachedUserId: string;
};

export type AdminSetAttachedUserDto = SetAttachedUserDto & {
  // Admin may override the once-per-billing-period change throttle.
  overrideThrottle?: boolean;
};

// D4 B-soft back-fill body for POST /membership-subscriptions/:id/accept-cgu.
export type AcceptCguDto = {
  acceptedCguVersion: number;
  acceptedCguSource: CguSource;
};
